import { type FieldSchemaJson } from 'application/Shared/Components/Form/Field.type';
import { RECIPE_ACTIONS } from 'application/Shared/Constants/Workato';
import { debug as logger } from 'application/Shared/Utils';
import axios from 'axios';
import { useEffect, useState } from 'react';

/**
 * Interface for the parameters of the runRecipe function
 */
export interface RecipeDetailsParams {
    /** The action to run */
    action: RECIPE_ACTIONS;
    /** The ID of the application */
    applicationId: string;
    /** The ID of the platform tenant */
    platformTenantId: string;
}

/**
 * Interface for the return value of the runRecipe function
 */
export interface RecipeDetails {
    /** Description of the recipe */
    description: string;
    /** Name of the recipe */
    name: string;
    /** Schema for the request/form */
    requestSchema: FieldSchemaJson[];
}

/**
 * Custom hook for getting the details of a customer recipe.
 *
 * @param {RecipeDetailsParams} params - The parameters for fetching the recipe details
 * @param {string} params.platformTenantId - The ID of the platform tenant
 * @param {RECIPE_ACTIONS} params.action - The action to run
 * @param {string} params.applicationId - The ID of the application
 *
 * @returns An object containing the recipe details and loading status
 *
 * @example
 * const { data, error, loading } = useRecipeDetails({
 *    platformTenantId: '1234',
 *    action: 'offboard',
 *    applicationId: '5678'
 * });
 */
export function useRecipeDetails({
    platformTenantId,
    action,
    applicationId
}: RecipeDetailsParams) {
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<RecipeDetails | null>(null);

    useEffect(() => {
        const { CancelToken } = axios;
        const { token: cancelToken } = CancelToken.source();

        const fetchData = async () => {
            setLoading(true);
            try {
                // Check for required parameters, and throw if they are missing.
                if (!platformTenantId || !action || !applicationId) {
                    throw new Error('Missing required parameters');
                }

                const response = await axios.get(
                    '/workato/embedded/recipe/details',
                    {
                        cancelToken,
                        params: { action, applicationId, platformTenantId }
                    }
                );
                setData(response.data?.data);
            } catch (error) {
                setError(
                    'Error fetching customer connections. Please try again later.'
                );
                logger.error('Error fetching customer connections.', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [platformTenantId, applicationId, action]);

    return { data, error, loading };
}
